import { useState, useEffect } from 'react';
import {
    Show,
    useShowContext,
    useNotify,
    useRefresh,
    usePermissions,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Typography,
    Button,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    alpha,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonAddIcon from '@mui/icons-material/PersonAddOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import BadgeIcon from '@mui/icons-material/BadgeOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import MemoryIcon from '@mui/icons-material/MemoryOutlined';
import KeyIcon from '@mui/icons-material/KeyOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import BugReportIcon from '@mui/icons-material/BugReportOutlined';
import ActivityIcon from '@mui/icons-material/TimelineOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import HourglassIcon from '@mui/icons-material/HourglassFullOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import DevicesIcon from '@mui/icons-material/DevicesOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme';
import { DiagnosticsTab } from './DiagnosticsTab';
import { ShellPasswordPanel } from './ShellPasswordPanel';
import { DeviceKeyActionButtons } from './DeviceKeyActionButtons';
import { hasSupportRole } from './supportRole';

// Types
interface Controller {
    controllerId: string;
    controllerName: string | null;
    isActive: boolean;
    transactionInProgress: boolean;
    lastCollectionDate: string | null;
    startHistoryDate: string | null;
    startAlarmDetailDate: string | null;
}

interface ApiKey {
    id: number;
    keyPrefix: string;
    isActive: boolean;
    isExpired: boolean;
    createdAt: string;
    lastUsedAt: string | null;
    expiresAt: string | null;
    revokedAt: string | null;
    revokedBy: string | null;
    revokedReason: string | null;
}

interface NotificationTarget {
    recordId: number;
    emailAddress: string | null;
    emailNotificationsOn: boolean;
    smsAddress: string | null;
    smsNotificationsOn: boolean;
}

interface SessionStatus {
    sessionId: string;
    status: string;
    techSupportUserName: string | null;
    techSupportUserEmail: string | null;
    isOwner: boolean;
    requestedAt: string;
    startedAt: string | null;
    endedAt: string | null;
    timeRemaining: string | null;
    approvalExpiresInMinutes: number | null;
    reason: string | null;
    ticketNumber: string | null;
    approvedBy: string | null;
    denialReason: string | null;
    terminatedBy: string | null;
    terminationReason: string | null;
    canCancel: boolean;
    canTerminate: boolean;
    canDismiss: boolean;
}

interface DeviceDetail {
    id: string;
    nodeId: string;
    nodeName: string | null;
    isActive: boolean;
    isExpired: boolean;
    isRevoked: boolean;
    status: string;
    hasActiveKey: boolean;
    provisionedAt: string;
    provisionedVia: string | null;
    provisionedViaDisplay: string;
    provisioningTokenName: string | null;
    provisionedFromIp: string | null;
    lastSeenAt: string | null;
    lastSeenDisplay: string;
    expiresAt: string | null;
    daysUntilExpiry: number | null;
    revokedAt: string | null;
    revokedReason: string | null;
    revokedBy: string | null;
    ownerId: string | null;
    ownerName: string | null;
    ownerEmail: string | null;
    hasAccessPoint: boolean;
    collectionCadence: number;
    verboseLoggingEnabled: boolean;
    enterpriseId: number | null;
    registrationRestricted: boolean;
    controllerCount: number;
    activeControllerCount: number;
    apiKeyCount: number;
    activeApiKeyCount: number;
    notificationTargetCount: number;
    controllers: Controller[];
    apiKeys: ApiKey[];
    notificationTargets: NotificationTarget[];
    sessionStatus: SessionStatus | null;
}

// KPI Card Component
const KpiCard = ({ title, value, icon, color }: { title: string; value: string | number; icon: React.ReactNode; color: string }) => (
    <Card sx={{ borderRadius: '12px', border: `1px solid ${colors.slate[100]}` }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" alignItems="center" gap={2}>
                <Box sx={{
                    width: 44, height: 44, borderRadius: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: alpha(color, 0.1), color: color,
                }}>
                    {icon}
                </Box>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: colors.slate[900], lineHeight: 1 }}>
                        {value}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.slate[500], fontSize: 13, mt: 0.5 }}>
                        {title}
                    </Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// Status Badge Component
const StatusBadge = ({ device }: { device: DeviceDetail }) => {
    if (device.isRevoked) {
        return (
            <Chip
                icon={<CancelIcon sx={{ fontSize: 16 }} />}
                label="Revoked"
                sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error, fontWeight: 600 }}
            />
        );
    }
    if (device.isExpired) {
        return (
            <Chip
                icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                label="Expired"
                sx={{ bgcolor: colors.slate[100], color: colors.slate[500], fontWeight: 600 }}
            />
        );
    }
    if (device.isActive) {
        return (
            <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                label="Active"
                sx={{ bgcolor: alpha(colors.success, 0.1), color: colors.success, fontWeight: 600 }}
            />
        );
    }
    return (
        <Chip
            icon={<HelpOutlineIcon sx={{ fontSize: 16 }} />}
            label="Inactive"
            sx={{ bgcolor: colors.slate[100], color: colors.slate[500], fontWeight: 600 }}
        />
    );
};

// Helper functions for countdown timer
const parseTimeToSeconds = (timeRemaining: string | null): number | null => {
    if (!timeRemaining) return null;
    const parts = timeRemaining.split(':');
    if (parts.length === 3) {
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        const seconds = parseFloat(parts[2]);
        return hours * 3600 + minutes * 60 + Math.floor(seconds);
    }
    return null;
};

const formatSeconds = (totalSeconds: number): string => {
    if (totalSeconds <= 0) return 'Expired';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};

const formatStartedDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        month: 'short', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
    });
};

// Session Status Panel Component
const SessionStatusPanel = ({ session, onRefresh, onDismiss }: { session: SessionStatus; onRefresh: () => void; onDismiss?: () => void }) => {
    const notify = useNotify();
    const [cancelling, setCancelling] = useState(false);
    const [ending, setEnding] = useState(false);

    const isActive = session.status === 'Active';
    const isTerminal = ['Expired', 'Terminated', 'Cancelled', 'Denied'].includes(session.status);
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(() =>
        parseTimeToSeconds(session.timeRemaining)
    );

    // Live countdown effect
    useEffect(() => {
        if (!isActive || remainingSeconds === null || remainingSeconds <= 0) return;
        const interval = setInterval(() => {
            setRemainingSeconds(prev => {
                if (prev === null || prev <= 0) return prev;
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [isActive, remainingSeconds]);

    const statusColors: Record<string, { bg: string; border: string; text: string }> = {
        PendingApproval: { bg: 'rgba(245, 158, 11, 0.08)', border: '#f59e0b', text: '#d97706' },
        Active: { bg: 'rgba(5, 150, 105, 0.08)', border: '#059669', text: '#059669' },
        Denied: { bg: 'rgba(220, 38, 38, 0.08)', border: '#dc2626', text: '#dc2626' },
        Expired: { bg: 'rgba(100, 116, 139, 0.08)', border: '#64748b', text: '#64748b' },
        Terminated: { bg: 'rgba(139, 92, 246, 0.08)', border: '#8b5cf6', text: '#8b5cf6' },
        Cancelled: { bg: 'rgba(100, 116, 139, 0.08)', border: '#94a3b8', text: '#64748b' },
    };

    const style = statusColors[session.status] || statusColors.Expired;

    const statusLabels: Record<string, string> = {
        PendingApproval: 'Pending Approval',
        Active: 'Session Active',
        Denied: 'Request Denied',
        Expired: 'Session Expired',
        Terminated: 'Session Terminated',
        Cancelled: 'Request Cancelled',
    };

    const handleCancel = async () => {
        setCancelling(true);
        try {
            const response = await fetch('/api/TechSupport/terminate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId: session.sessionId,
                    reason: 'Request cancelled via Admin Console',
                }),
            });
            if (response.ok) {
                notify('Request cancelled', { type: 'success' });
                onRefresh();
            } else {
                const error = await response.json().catch(() => ({}));
                notify(error.message || 'Failed to cancel request', { type: 'error' });
            }
        } catch {
            notify('Failed to cancel request', { type: 'error' });
        } finally {
            setCancelling(false);
        }
    };

    const handleEnd = async () => {
        setEnding(true);
        try {
            const response = await fetch('/api/TechSupport/terminate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId: session.sessionId,
                    reason: 'Session terminated via Admin Console',
                }),
            });
            if (response.ok) {
                notify('Session ended', { type: 'success' });
                onRefresh();
            } else {
                const error = await response.json().catch(() => ({}));
                notify(error.message || 'Failed to end session', { type: 'error' });
            }
        } catch {
            notify('Failed to end session', { type: 'error' });
        } finally {
            setEnding(false);
        }
    };

    return (
        <Box sx={{
            borderRadius: '12px', p: 2.5, mb: 3,
            bgcolor: style.bg, borderLeft: `4px solid ${style.border}`,
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Chip
                    icon={<BadgeIcon sx={{ fontSize: 16 }} />}
                    label={statusLabels[session.status] || session.status}
                    sx={{ bgcolor: alpha(style.border, 0.15), color: style.text, fontWeight: 600 }}
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                    <Typography variant="caption" sx={{ color: colors.slate[500] }}>Tech Support</Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                        {session.techSupportUserName}
                        {session.techSupportUserEmail && (
                            <Typography component="span" sx={{ color: colors.slate[500], ml: 1 }}>
                                ({session.techSupportUserEmail})
                            </Typography>
                        )}
                        {session.isOwner && (
                            <Chip label="You" size="small" sx={{ ml: 1, bgcolor: colors.primary, color: 'white', fontSize: 11 }} />
                        )}
                    </Typography>
                </Box>

                {isActive && session.startedAt && (
                    <Box>
                        <Typography variant="caption" sx={{ color: colors.slate[500] }}>Started</Typography>
                        <Typography sx={{ fontWeight: 500 }}>{formatStartedDate(session.startedAt)}</Typography>
                    </Box>
                )}

                {isActive && remainingSeconds !== null && (
                    <Box>
                        <Typography variant="caption" sx={{ color: colors.slate[500] }}>Time Remaining</Typography>
                        <Typography sx={{
                            fontWeight: 600,
                            fontFamily: '"JetBrains Mono", monospace',
                            color: remainingSeconds <= 300 ? colors.error : colors.slate[900],
                        }}>
                            {formatSeconds(remainingSeconds)}
                        </Typography>
                    </Box>
                )}

                {session.status === 'PendingApproval' && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Waiting for device owner to approve via email.
                    </Alert>
                )}

                {session.ticketNumber && (
                    <Box>
                        <Typography variant="caption" sx={{ color: colors.slate[500] }}>Ticket</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{session.ticketNumber}</Typography>
                    </Box>
                )}

                {session.reason && (
                    <Box>
                        <Typography variant="caption" sx={{ color: colors.slate[500] }}>Reason</Typography>
                        <Typography>{session.reason}</Typography>
                    </Box>
                )}
            </Box>

            {(session.canCancel || session.canTerminate) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)', display: 'flex', gap: 1.5 }}>
                    {session.canCancel && (
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleCancel}
                            disabled={cancelling}
                            startIcon={cancelling ? <CircularProgress size={16} /> : <CloseIcon />}
                        >
                            Cancel Request
                        </Button>
                    )}
                    {session.canTerminate && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleEnd}
                            disabled={ending}
                            startIcon={ending ? <CircularProgress size={16} /> : <CloseIcon />}
                        >
                            End Session
                        </Button>
                    )}
                </Box>
            )}

            {isTerminal && onDismiss && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={onDismiss}
                        sx={{ color: colors.slate[500] }}
                    >
                        Dismiss
                    </Button>
                </Box>
            )}
        </Box>
    );
};

// Overview Tab Component
const OverviewTab = ({ device, canSupport }: { device: DeviceDetail; canSupport: boolean }) => {
    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleString();
    };

    const InfoSection = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
        <Box sx={{ bgcolor: colors.slate[50], borderRadius: '12px', p: 2.5, mb: 3 }}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: colors.slate[700], mb: 2 }}>
                <Box sx={{ color: colors.primary }}>{icon}</Box>
                {title}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                {children}
            </Box>
        </Box>
    );

    const InfoItem = ({ label, value, fullWidth = false }: { label: string; value: React.ReactNode; fullWidth?: boolean }) => (
        <Box sx={{ gridColumn: fullWidth ? 'span 2' : 'span 1' }}>
            <Typography variant="caption" sx={{ color: colors.slate[500], textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography sx={{ color: colors.slate[900], mt: 0.5 }}>{value}</Typography>
        </Box>
    );

    const getProvisioningIcon = (via: string | null) => {
        switch (via?.toLowerCase()) {
            case 'bootstrap': return <KeyIcon sx={{ fontSize: 16 }} />;
            case 'manual': return <PersonIcon sx={{ fontSize: 16 }} />;
            case 'recovery': return <ScheduleIcon sx={{ fontSize: 16 }} />;
            default: return <DevicesIcon sx={{ fontSize: 16 }} />;
        }
    };

    return (
        <Box>
            <InfoSection title="Device Information" icon={<DevicesIcon />}>
                <InfoItem label="Node ID" value={<code style={{ background: 'white', padding: '4px 10px', borderRadius: '6px', border: `1px solid ${colors.slate[200]}` }}>{device.nodeId}</code>} />
                <InfoItem label="Device Name" value={device.nodeName || '—'} />
                <InfoItem label="Owner" value={device.ownerName || device.ownerId || '—'} />
                <InfoItem label="Enterprise ID" value={device.enterpriseId?.toString() || '—'} />
            </InfoSection>

            <InfoSection title="Provisioning" icon={<SettingsIcon />}>
                <InfoItem label="Provisioned" value={formatDate(device.provisionedAt)} />
                <InfoItem
                    label="Method"
                    value={
                        <Chip
                            size="small"
                            icon={getProvisioningIcon(device.provisionedVia)}
                            label={device.provisionedViaDisplay}
                            sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary }}
                        />
                    }
                />
                {device.provisioningTokenName && (
                    <InfoItem label="Token Name" value={<code style={{ background: 'white', padding: '2px 8px', borderRadius: '4px' }}>{device.provisioningTokenName}</code>} />
                )}
                {device.provisionedFromIp && (
                    <InfoItem label="Source IP" value={<code style={{ background: 'white', padding: '2px 8px', borderRadius: '4px' }}>{device.provisionedFromIp}</code>} />
                )}
            </InfoSection>

            {canSupport && (
                <Box sx={{ mb: 3 }}>
                    <ShellPasswordPanel nodeId={device.nodeId} />
                </Box>
            )}

            <InfoSection title="Status" icon={<ActivityIcon />}>
                <InfoItem label="Status" value={<StatusBadge device={device} />} />
                <InfoItem
                    label="Last Seen"
                    value={
                        device.lastSeenAt ? (
                            <Box>
                                {formatDate(device.lastSeenAt)}
                                <Typography component="span" sx={{ color: colors.slate[400], ml: 1 }}>
                                    ({device.lastSeenDisplay})
                                </Typography>
                            </Box>
                        ) : (
                            <Typography sx={{ color: colors.slate[400], fontStyle: 'italic' }}>Never connected</Typography>
                        )
                    }
                />
                <InfoItem
                    label="Expires"
                    value={
                        device.expiresAt ? (
                            <Box>
                                <Typography
                                    component="span"
                                    sx={{
                                        color: device.isExpired ? colors.error
                                            : (device.daysUntilExpiry && device.daysUntilExpiry <= 7) ? colors.error
                                            : (device.daysUntilExpiry && device.daysUntilExpiry <= 30) ? colors.warning
                                            : colors.slate[900]
                                    }}
                                >
                                    {formatDate(device.expiresAt)}
                                </Typography>
                                {device.daysUntilExpiry && device.daysUntilExpiry > 0 && (
                                    <Typography component="span" sx={{ color: colors.slate[400], ml: 1 }}>
                                        ({device.daysUntilExpiry} days remaining)
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            <Typography sx={{ color: colors.slate[400], fontStyle: 'italic' }}>No expiration</Typography>
                        )
                    }
                />
            </InfoSection>

            {device.isRevoked && (
                <Box sx={{ bgcolor: alpha(colors.error, 0.05), borderRadius: '12px', p: 2.5, border: `1px solid ${alpha(colors.error, 0.2)}` }}>
                    <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: colors.error, mb: 2 }}>
                        <CancelIcon /> Revocation Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                        <InfoItem label="Revoked At" value={formatDate(device.revokedAt)} />
                        <InfoItem label="Revoked By" value={device.revokedBy || '—'} />
                        <InfoItem label="Reason" value={device.revokedReason || '—'} fullWidth />
                    </Box>
                </Box>
            )}

            {device.hasAccessPoint && (
                <InfoSection title="Settings" icon={<SettingsIcon />}>
                    <InfoItem label="Collection Cadence" value={`${device.collectionCadence} minutes`} />
                    <InfoItem
                        label="Verbose Logging"
                        value={
                            <Chip
                                size="small"
                                label={device.verboseLoggingEnabled ? 'Enabled' : 'Disabled'}
                                sx={{
                                    bgcolor: device.verboseLoggingEnabled ? alpha(colors.success, 0.1) : colors.slate[100],
                                    color: device.verboseLoggingEnabled ? colors.success : colors.slate[500],
                                }}
                            />
                        }
                    />
                    <InfoItem
                        label="Registration Restricted"
                        value={
                            <Chip
                                size="small"
                                label={device.registrationRestricted ? 'Restricted' : 'Open'}
                                sx={{
                                    bgcolor: device.registrationRestricted ? alpha(colors.warning, 0.1) : alpha(colors.success, 0.1),
                                    color: device.registrationRestricted ? colors.warning : colors.success,
                                }}
                            />
                        }
                    />
                </InfoSection>
            )}
        </Box>
    );
};

// Controllers Tab Component
const ControllersTab = ({ controllers, hasAccessPoint }: { controllers: Controller[]; hasAccessPoint: boolean }) => {
    if (!hasAccessPoint) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box sx={{ width: 64, height: 64, bgcolor: alpha(colors.primary, 0.1), borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <MemoryIcon sx={{ fontSize: 28, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>No Controllers Available</Typography>
                <Typography sx={{ color: colors.slate[500] }}>This device has not registered as an Access Point yet.</Typography>
            </Box>
        );
    }

    if (controllers.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box sx={{ width: 64, height: 64, bgcolor: alpha(colors.primary, 0.1), borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <MemoryIcon sx={{ fontSize: 28, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>No Controllers Configured</Typography>
                <Typography sx={{ color: colors.slate[500] }}>This device doesn't have any controllers configured yet.</Typography>
            </Box>
        );
    }

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Chip label={`${controllers.length} controller(s)`} sx={{ bgcolor: colors.slate[100], color: colors.slate[500] }} />
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Controller ID</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Last Collection</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>History Start</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Alarm Start</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {controllers.map((c) => (
                            <TableRow key={c.controllerId} sx={{ bgcolor: c.transactionInProgress ? alpha(colors.warning, 0.05) : 'transparent' }}>
                                <TableCell>
                                    <code style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, padding: '4px 8px', background: colors.slate[100], borderRadius: '6px' }}>
                                        {c.controllerId}
                                    </code>
                                </TableCell>
                                <TableCell>{c.controllerName || <Typography sx={{ color: colors.slate[400], fontStyle: 'italic' }}>—</Typography>}</TableCell>
                                <TableCell>
                                    {c.transactionInProgress ? (
                                        <Chip size="small" icon={<HourglassIcon sx={{ fontSize: 14 }} />} label="Transaction" sx={{ bgcolor: alpha(colors.warning, 0.1), color: colors.warning }} />
                                    ) : c.isActive ? (
                                        <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Active" sx={{ bgcolor: alpha(colors.success, 0.1), color: colors.success }} />
                                    ) : (
                                        <Chip size="small" label="Idle" sx={{ bgcolor: colors.slate[100], color: colors.slate[500] }} />
                                    )}
                                </TableCell>
                                <TableCell>{c.lastCollectionDate ? formatDate(c.lastCollectionDate) : <Typography sx={{ color: colors.slate[400], fontStyle: 'italic' }}>Never</Typography>}</TableCell>
                                <TableCell>{formatDate(c.startHistoryDate)}</TableCell>
                                <TableCell>{formatDate(c.startAlarmDetailDate)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

// API Keys Tab Component
const ApiKeysTab = ({
    apiKeys,
    nodeId,
    canManage,
    onChanged,
}: {
    apiKeys: ApiKey[];
    nodeId: string;
    canManage: boolean;
    onChanged: () => void;
}) => {
    if (apiKeys.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box sx={{ width: 64, height: 64, bgcolor: alpha(colors.primary, 0.1), borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <KeyIcon sx={{ fontSize: 28, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>No API Keys</Typography>
                <Typography sx={{ color: colors.slate[500] }}>This device doesn't have any API keys.</Typography>
            </Box>
        );
    }

    const activeCount = apiKeys.filter(k => k.isActive && !k.revokedAt && !k.isExpired).length;
    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const getLastUsedDisplay = (lastUsedAt: string | null) => {
        if (!lastUsedAt) return <Typography sx={{ color: colors.slate[400], fontStyle: 'italic' }}>Never used</Typography>;
        const elapsed = Date.now() - new Date(lastUsedAt).getTime();
        const minutes = Math.floor(elapsed / 60000);
        const hours = Math.floor(elapsed / 3600000);
        const days = Math.floor(elapsed / 86400000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes} min ago`;
        if (hours < 24) return `${hours} hr ago`;
        if (days < 7) return `${days} days ago`;
        return formatDate(lastUsedAt);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={`${apiKeys.length} key(s)`} sx={{ bgcolor: colors.slate[100], color: colors.slate[500] }} />
                <Chip label={`${activeCount} active`} sx={{ bgcolor: alpha(colors.success, 0.1), color: colors.success }} />
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Key Prefix</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Created</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Last Used</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Expires</TableCell>
                            {canManage && (
                                <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Actions</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {apiKeys.map((key) => {
                            const isInactive = key.revokedAt || key.isExpired || !key.isActive;
                            const daysUntil = key.expiresAt ? (new Date(key.expiresAt).getTime() - Date.now()) / 86400000 : null;

                            return (
                                <TableRow key={key.id} sx={{ opacity: isInactive ? 0.6 : 1 }}>
                                    <TableCell>
                                        <code style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, padding: '4px 8px', background: colors.slate[100], borderRadius: '6px' }}>
                                            {key.keyPrefix}
                                        </code>
                                    </TableCell>
                                    <TableCell>
                                        {key.revokedAt ? (
                                            <Tooltip title={key.revokedReason || 'Revoked'}>
                                                <Chip size="small" icon={<CancelIcon sx={{ fontSize: 14 }} />} label="Revoked" sx={{ bgcolor: alpha(colors.error, 0.1), color: colors.error }} />
                                            </Tooltip>
                                        ) : key.isExpired ? (
                                            <Chip size="small" icon={<ScheduleIcon sx={{ fontSize: 14 }} />} label="Expired" sx={{ bgcolor: colors.slate[100], color: colors.slate[500] }} />
                                        ) : key.isActive ? (
                                            <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Active" sx={{ bgcolor: alpha(colors.success, 0.1), color: colors.success }} />
                                        ) : (
                                            <Chip size="small" label="Inactive" sx={{ bgcolor: colors.slate[100], color: colors.slate[500] }} />
                                        )}
                                    </TableCell>
                                    <TableCell>{formatDate(key.createdAt)}</TableCell>
                                    <TableCell>{getLastUsedDisplay(key.lastUsedAt)}</TableCell>
                                    <TableCell>
                                        {key.expiresAt ? (
                                            <Box>
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        color: key.isExpired ? colors.error
                                                            : (daysUntil && daysUntil <= 7) ? colors.error
                                                            : (daysUntil && daysUntil <= 30) ? colors.warning
                                                            : colors.slate[600],
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    {new Date(key.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </Typography>
                                                {daysUntil && daysUntil > 0 && daysUntil <= 30 && !key.revokedAt && (
                                                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: daysUntil <= 7 ? colors.error : colors.warning }}>
                                                        {Math.floor(daysUntil)} days left
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography sx={{ color: colors.slate[400], fontStyle: 'italic' }}>No expiration</Typography>
                                        )}
                                    </TableCell>
                                    {canManage && (
                                        <TableCell>
                                            <DeviceKeyActionButtons
                                                nodeId={nodeId}
                                                keyId={key.id}
                                                disabled={Boolean(isInactive)}
                                                onChanged={onChanged}
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Alert severity="info" sx={{ mt: 3 }}>
                <strong>Security Note:</strong> API keys are never stored in plain text. The key value is only shown once when generated.
            </Alert>
        </Box>
    );
};

// Notifications Tab Component
const NotificationsTab = ({ targets, hasAccessPoint }: { targets: NotificationTarget[]; hasAccessPoint: boolean }) => {
    if (!hasAccessPoint) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box sx={{ width: 64, height: 64, bgcolor: alpha(colors.primary, 0.1), borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <NotificationsIcon sx={{ fontSize: 28, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>No Notifications Available</Typography>
                <Typography sx={{ color: colors.slate[500] }}>This device has not registered as an Access Point yet.</Typography>
            </Box>
        );
    }

    if (targets.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box sx={{ width: 64, height: 64, bgcolor: alpha(colors.primary, 0.1), borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <NotificationsIcon sx={{ fontSize: 28, color: colors.primary }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>No Notification Targets</Typography>
                <Typography sx={{ color: colors.slate[500] }}>This device doesn't have any notification targets configured.</Typography>
            </Box>
        );
    }

    const emailCount = targets.filter(t => t.emailNotificationsOn).length;
    const smsCount = targets.filter(t => t.smsNotificationsOn).length;

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={`${targets.length} target(s)`} sx={{ bgcolor: colors.slate[100], color: colors.slate[500] }} />
                {emailCount > 0 && <Chip icon={<EmailIcon sx={{ fontSize: 14 }} />} label={`${emailCount} email`} sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary }} />}
                {smsCount > 0 && <Chip icon={<PhoneIcon sx={{ fontSize: 14 }} />} label={`${smsCount} SMS`} sx={{ bgcolor: alpha(colors.secondary, 0.1), color: colors.secondary }} />}
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Email Address</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>Email Notifications</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>SMS Address</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.slate[600] }}>SMS Notifications</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {targets.map((target) => {
                            const isInactive = !target.emailNotificationsOn && !target.smsNotificationsOn;
                            return (
                                <TableRow key={target.recordId} sx={{ opacity: isInactive ? 0.6 : 1 }}>
                                    <TableCell>
                                        {target.emailAddress ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <EmailIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
                                                {target.emailAddress}
                                            </Box>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {target.emailAddress ? (
                                            <Chip
                                                size="small"
                                                label={target.emailNotificationsOn ? 'Enabled' : 'Disabled'}
                                                sx={{
                                                    bgcolor: target.emailNotificationsOn ? alpha(colors.success, 0.1) : colors.slate[100],
                                                    color: target.emailNotificationsOn ? colors.success : colors.slate[500],
                                                }}
                                            />
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {target.smsAddress ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PhoneIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
                                                {target.smsAddress}
                                            </Box>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {target.smsAddress ? (
                                            <Chip
                                                size="small"
                                                label={target.smsNotificationsOn ? 'Enabled' : 'Disabled'}
                                                sx={{
                                                    bgcolor: target.smsNotificationsOn ? alpha(colors.success, 0.1) : colors.slate[100],
                                                    color: target.smsNotificationsOn ? colors.success : colors.slate[500],
                                                }}
                                            />
                                        ) : '—'}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Alert severity="info" sx={{ mt: 3 }}>
                Notification targets receive alerts when alarms are triggered on this device's controllers.
            </Alert>
        </Box>
    );
};

// Main Device Show Content Component
const DeviceShowContent = () => {
    const { record, isPending } = useShowContext<DeviceDetail>();
    // #1542. The server gates these on the TechSupport role, so the console hides them on the
    // same answer. Showing a control that is certain to 403 teaches the operator to ignore
    // errors, and it files an audited refusal for something they were invited to click.
    const { permissions } = usePermissions();
    const canSupport = hasSupportRole(permissions);
    const navigate = useNavigate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [tab, setTab] = useState(0);

    // Modal states
    const [reassignOpen, setReassignOpen] = useState(false);
    const [extendOpen, setExtendOpen] = useState(false);
    const [impersonateOpen, setImpersonateOpen] = useState(false);

    // Form states
    const [newOwnerEmail, setNewOwnerEmail] = useState('');
    const [reassignReason, setReassignReason] = useState('');
    const [extendDays, setExtendDays] = useState(30);
    const [extendReason, setExtendReason] = useState('');
    const [sessionDuration, setSessionDuration] = useState(120);
    const [ticketNumber, setTicketNumber] = useState('');
    const [impersonateReason, setImpersonateReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Track dismissed session IDs in localStorage (persists across navigation, shared across all devices)
    const [, forceUpdate] = useState(0);
    const dismissedSessionsKey = 'xtconnect-dismissed-sessions';
    const getDismissedSessions = (): string[] => {
        try {
            return JSON.parse(localStorage.getItem(dismissedSessionsKey) || '[]');
        } catch {
            return [];
        }
    };
    const isSessionDismissed = (sessionId: string) => getDismissedSessions().includes(sessionId);
    const dismissSession = (sessionId: string) => {
        const dismissed = getDismissedSessions();
        if (!dismissed.includes(sessionId)) {
            // Keep only last 50 dismissed sessions to prevent unbounded growth
            const updated = [...dismissed, sessionId].slice(-50);
            localStorage.setItem(dismissedSessionsKey, JSON.stringify(updated));
        }
        // Force re-render
        forceUpdate(prev => prev + 1);
    };

    if (isPending || !record) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const device = record;

    const handleReassign = async () => {
        setSubmitting(true);
        try {
            const response = await fetch(`/api/admin/devices/${device.nodeId}/reassign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ newOwnerEmail, reason: reassignReason }),
            });
            if (response.ok) {
                notify('Ownership transferred successfully', { type: 'success' });
                setReassignOpen(false);
                refresh();
            } else {
                const data = await response.json();
                notify(data.message || 'Failed to transfer ownership', { type: 'error' });
            }
        } catch {
            notify('Failed to transfer ownership', { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleExtend = async () => {
        setSubmitting(true);
        try {
            const response = await fetch(`/api/admin/devices/${device.nodeId}/extend`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ days: extendDays, reason: extendReason }),
            });
            if (response.ok) {
                notify(`Expiry extended by ${extendDays} days`, { type: 'success' });
                setExtendOpen(false);
                refresh();
            } else {
                const data = await response.json();
                notify(data.message || 'Failed to extend expiry', { type: 'error' });
            }
        } catch {
            notify('Failed to extend expiry', { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleImpersonate = async () => {
        setSubmitting(true);
        try {
            const response = await fetch('/api/TechSupport/impersonate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    targetUserId: device.ownerId,
                    targetUserEmail: device.ownerEmail,
                    reason: impersonateReason || 'Support session requested from Admin Console',
                    ticketNumber: ticketNumber || null,
                    expectedDurationMinutes: sessionDuration,
                }),
            });
            if (response.ok) {
                notify('Impersonation request sent to device owner for approval', { type: 'success' });
                setImpersonateOpen(false);
                setTicketNumber('');
                setImpersonateReason('');
                setSessionDuration(120);
                refresh();
            } else {
                const data = await response.json();
                notify(data.message || 'Failed to request impersonation', { type: 'error' });
            }
        } catch {
            notify('Failed to request impersonation', { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const canReassign = device.hasAccessPoint;
    const canExtend = device.isActive && !device.isRevoked;
    // Only block impersonation for active or pending sessions
    const activeSessionStatuses = ['Active', 'PendingApproval'];
    const hasActiveSession = device.sessionStatus && activeSessionStatuses.includes(device.sessionStatus.status);
    const canImpersonate = device.ownerId && device.isActive && !device.isRevoked && !hasActiveSession;

    return (
        <Box>
            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: colors.slate[900] }}>
                            {device.nodeName || <code style={{ fontFamily: '"JetBrains Mono", monospace' }}>{device.nodeId}</code>}
                        </Typography>
                        <StatusBadge device={device} />
                    </Box>
                    {device.nodeName && (
                        <Typography sx={{ color: colors.slate[500] }}>
                            Node ID: <code>{device.nodeId}</code>
                        </Typography>
                    )}
                    {!device.nodeName && (
                        <Typography sx={{ color: colors.slate[500] }}>
                            Device provisioned {new Date(device.provisionedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                    )}
                    {device.ownerId && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <PersonIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
                            <Typography sx={{ color: colors.slate[500] }}>Owner:</Typography>
                            <Typography sx={{ color: colors.primary, fontWeight: 500 }}>
                                {device.ownerName || device.ownerEmail || device.ownerId}
                            </Typography>
                        </Box>
                    )}
                    {!device.ownerId && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <PersonIcon sx={{ fontSize: 16, color: colors.slate[400] }} />
                            <Typography sx={{ color: colors.slate[500] }}>Owner:</Typography>
                            <Typography sx={{ color: colors.slate[400], fontStyle: 'italic' }}>Not registered</Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {canReassign && (
                        <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setReassignOpen(true)}>
                            Re-assign Owner
                        </Button>
                    )}
                    {canExtend && (
                        <Button variant="outlined" startIcon={<CalendarMonthIcon />} onClick={() => setExtendOpen(true)}>
                            Extend Expiry
                        </Button>
                    )}
                    {canImpersonate && (
                        <Button variant="outlined" startIcon={<BadgeIcon />} onClick={() => setImpersonateOpen(true)}>
                            Impersonate
                        </Button>
                    )}
                    {hasActiveSession && (
                        <Chip
                            icon={<BadgeIcon />}
                            label={device.sessionStatus!.status === 'Active' ? 'Session Active' : 'Pending Approval'}
                            sx={{
                                bgcolor: device.sessionStatus!.status === 'Active' ? alpha(colors.success, 0.15) : alpha(colors.warning, 0.15),
                                color: device.sessionStatus!.status === 'Active' ? colors.success : colors.warning,
                                fontWeight: 600,
                                py: 2.5,
                            }}
                        />
                    )}
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/devices')}>
                        Back to Provisioned Devices
                    </Button>
                </Box>
            </Box>

            {/* Session Status Panel */}
            {device.sessionStatus && !isSessionDismissed(device.sessionStatus.sessionId) && (
                <SessionStatusPanel
                    session={device.sessionStatus}
                    onRefresh={refresh}
                    onDismiss={() => dismissSession(device.sessionStatus!.sessionId)}
                />
            )}

            {/* KPI Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
                <KpiCard title="Controllers" value={device.controllerCount} icon={<MemoryIcon />} color={colors.primary} />
                <KpiCard title="Active Keys" value={device.activeApiKeyCount} icon={<KeyIcon />} color={colors.tertiary} />
                <KpiCard title="Notifications" value={device.notificationTargetCount} icon={<NotificationsIcon />} color={colors.success} />
                <KpiCard
                    title="Last Seen"
                    value={device.lastSeenDisplay}
                    icon={<ActivityIcon />}
                    color={device.lastSeenAt && new Date(device.lastSeenAt).getTime() > Date.now() - 86400000 ? colors.success : colors.warning}
                />
            </Box>

            {/* Tabs */}
            <Card sx={{ borderRadius: '12px', border: `1px solid ${colors.slate[100]}` }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        borderBottom: `1px solid ${colors.slate[100]}`,
                        px: 1,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            minHeight: 56,
                        },
                    }}
                >
                    <Tab icon={<InfoIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview" />
                    <Tab icon={<MemoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`Controllers (${device.controllerCount})`} />
                    <Tab icon={<KeyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`API Keys (${device.apiKeyCount})`} />
                    <Tab icon={<NotificationsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={`Notifications (${device.notificationTargetCount})`} />
                    <Tab icon={<BugReportIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Diagnostics" />
                </Tabs>
                <CardContent sx={{ p: 3 }}>
                    {tab === 0 && <OverviewTab device={device} canSupport={canSupport} />}
                    {tab === 1 && <ControllersTab controllers={device.controllers} hasAccessPoint={device.hasAccessPoint} />}
                    {tab === 2 && (
                        <ApiKeysTab
                            apiKeys={device.apiKeys}
                            nodeId={device.nodeId}
                            canManage={canSupport}
                            onChanged={refresh}
                        />
                    )}
                    {tab === 3 && <NotificationsTab targets={device.notificationTargets} hasAccessPoint={device.hasAccessPoint} />}
                    {tab === 4 && <DiagnosticsTab nodeId={device.nodeId} />}
                </CardContent>
            </Card>

            {/* Re-assign Modal */}
            <Dialog open={reassignOpen} onClose={() => setReassignOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAddIcon sx={{ color: colors.primary }} />
                    Re-assign Device Owner
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ bgcolor: colors.slate[50], borderRadius: '10px', p: 2, mb: 3, mt: 1 }}>
                        <Typography sx={{ fontSize: 13 }}><strong>Device:</strong> <code>{device.nodeId}</code></Typography>
                        <Typography sx={{ fontSize: 13 }}><strong>Current Owner:</strong> {device.ownerName || 'Not registered'}</Typography>
                    </Box>
                    <TextField
                        label="New Owner Email"
                        type="email"
                        value={newOwnerEmail}
                        onChange={(e) => setNewOwnerEmail(e.target.value)}
                        fullWidth
                        required
                        sx={{ mb: 2 }}
                        helperText="The user must have an existing account."
                    />
                    <TextField
                        label="Reason for Transfer"
                        value={reassignReason}
                        onChange={(e) => setReassignReason(e.target.value)}
                        fullWidth
                        required
                        multiline
                        rows={3}
                        helperText="Minimum 10 characters. This will be recorded in the audit log."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: colors.slate[50] }}>
                    <Button onClick={() => setReassignOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleReassign}
                        disabled={submitting || !newOwnerEmail || reassignReason.length < 10}
                        startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                    >
                        Transfer Ownership
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Extend Expiry Modal */}
            <Dialog open={extendOpen} onClose={() => setExtendOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonthIcon sx={{ color: colors.primary }} />
                    Extend API Key Expiry
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ bgcolor: colors.slate[50], borderRadius: '10px', p: 2, mb: 3, mt: 1 }}>
                        <Typography sx={{ fontSize: 13 }}><strong>Device:</strong> <code>{device.nodeId}</code></Typography>
                        <Typography sx={{ fontSize: 13 }}>
                            <strong>Current Expiry:</strong>{' '}
                            {device.expiresAt ? new Date(device.expiresAt).toLocaleString() + ' UTC' : 'No expiration set'}
                        </Typography>
                    </Box>
                    <TextField
                        label="Extend By (Days)"
                        type="number"
                        value={extendDays}
                        onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                        fullWidth
                        required
                        sx={{ mb: 2 }}
                        inputProps={{ min: 1, max: 365 }}
                        helperText="Enter the number of days to extend (1-365)."
                    />
                    <TextField
                        label="Reason (Optional)"
                        value={extendReason}
                        onChange={(e) => setExtendReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        helperText="This will be recorded in the audit log."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: colors.slate[50] }}>
                    <Button onClick={() => setExtendOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleExtend}
                        disabled={submitting || extendDays < 1 || extendDays > 365}
                        startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                    >
                        Extend Expiry
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Impersonate Modal */}
            <Dialog open={impersonateOpen} onClose={() => setImpersonateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BadgeIcon sx={{ color: colors.primary }} />
                    Request Impersonation Session
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ bgcolor: colors.slate[50], borderRadius: '10px', p: 2, mb: 3, mt: 1 }}>
                        <Typography sx={{ fontSize: 13 }}><strong>Device:</strong> <code>{device.nodeId}</code></Typography>
                        <Typography sx={{ fontSize: 13 }}><strong>Owner:</strong> {device.ownerName || 'Unknown'}</Typography>
                    </Box>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <strong>Owner Approval Required</strong><br />
                        An email will be sent to the device owner requesting permission to access their account. They must approve before the session can begin.
                    </Alert>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Session Duration</InputLabel>
                        <Select
                            value={sessionDuration}
                            onChange={(e) => setSessionDuration(e.target.value as number)}
                            label="Session Duration"
                        >
                            <MenuItem value={30}>30 minutes</MenuItem>
                            <MenuItem value={60}>1 hour</MenuItem>
                            <MenuItem value={120}>2 hours (default)</MenuItem>
                            <MenuItem value={240}>4 hours</MenuItem>
                            <MenuItem value={480}>8 hours</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label="Support Ticket Number (Optional)"
                        value={ticketNumber}
                        onChange={(e) => setTicketNumber(e.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                        placeholder="e.g., SUPPORT-12345"
                        helperText="Reference to related support ticket for audit trail."
                    />
                    <TextField
                        label="Reason (Optional)"
                        value={impersonateReason}
                        onChange={(e) => setImpersonateReason(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Describe why you need to access this account..."
                        helperText="This will be shown to the device owner in the approval request."
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: colors.slate[50] }}>
                    <Button onClick={() => setImpersonateOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleImpersonate}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={16} /> : <BadgeIcon />}
                    >
                        Send Request
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export const DeviceShow = () => (
    <Show component="div" actions={false}>
        <DeviceShowContent />
    </Show>
);
